import React, { useState } from "react";
import ModalPortal from "@/components/Global/ModalPortal";
import { useTranslation } from "react-i18next";
import SRS from "@/services/StudentRecordService";
import { activityStatus } from "@/constants/constants";
import { makeToast, ToastTypes } from "@/components/Global/Notifications";
import { addBatchAssignments } from "@/services/assignment/addBatchAssignments";
import { updateBatchAssignments } from "@/services/assignment/updateBatchAssignments";
import { ASSIGNMENT_TYPE, ENTITY_TYPE } from "@/services/assignment/constants";
import { useCalendar } from "@/hooks/useCalendar";
import { AssignmentType } from "../AssessmentModal/AssessmentModal";
import "./ScoreEntryModal.scss";

interface ScoreEntryModalProps {
  isOpen: boolean;
  onClose: () => void;
  student: {
    studentId: string;
    firstName: string;
    lastName: string;
  };
  assignmentId?: string | null;
  districtId: number;
  schoolId: number;
  onScoreSubmitted?: () => void;
  selectedWindow?: {
    startDate: string | null;
    endDate: string | null;
    value: number | null;
    screeningWindowType: string | null;
    assignmentId: string | null;
    assignmentType: AssignmentType | null;
  };
}

interface ScoreData {
  dyslexiaRiskIndicator: string;
  dyslexiaRiskCategory: string;
  armScore: string;
  armScorePR: string;
  equatedWCPM: string;
  equatedWCPMPR: string;
  phonAwareScore: string;
  phonAwareScore_PR: string;
  decodeScore: string;
  decodeScore_PR: string;
  esriScore: string;
  esriScore_PR: string;
  vocabularyScreenerScore: string;
  vocabularyScreenerScore_PR: string;
  compScore: string;
  compScore_PR: string;
  bkScore: string;
  bkScore_PR: string;
}

interface ScoreRowProps {
  label: string;
  scoreField: keyof ScoreData;
  percentileField: keyof ScoreData;
  scorePlaceholder?: string;
  percentilePlaceholder?: string;
  scores: ScoreData;
  errors: Partial<ScoreData>;
  isSubmitting: boolean;
  onScoreChange: (field: keyof ScoreData, value: string) => void;
}

const ScoreRow: React.FC<ScoreRowProps> = ({ 
  label, 
  scoreField, 
  percentileField, 
  scorePlaceholder = "Enter score", 
  percentilePlaceholder = "Enter %",
  scores,
  errors,
  isSubmitting,
  onScoreChange
}) => (
  <div className="score-row">
    <div className="score-label">{label}</div>
    <div className="score-inputs">
      <div className="input-group">
        <input
          type="text"
          value={scores[scoreField]}
          onChange={(e) => onScoreChange(scoreField, e.target.value)}
          placeholder={scorePlaceholder}
          className={`score-input ${errors[scoreField] ? "error" : ""}`}
          disabled={isSubmitting}
        />
        {errors[scoreField] && <div className="field-error">{errors[scoreField]}</div>}
      </div>
      <div className="input-group">
        <input
          type="text"
          value={scores[percentileField]}
          onChange={(e) => onScoreChange(percentileField, e.target.value)}
          placeholder={percentilePlaceholder}
          className={`score-input ${errors[percentileField] ? "error" : ""}`}
          disabled={isSubmitting}
        />
        {errors[percentileField] && <div className="field-error">{errors[percentileField]}</div>}
      </div>
    </div>
  </div>
);

export const ScoreEntryModal: React.FC<ScoreEntryModalProps> = ({
  isOpen,
  onClose,
  student,
  assignmentId,
  districtId,
  schoolId,
  onScoreSubmitted,
  selectedWindow,
}) => {
  const { t } = useTranslation("dashboard");
  const { assessmentCalendar } = useCalendar();
  const currentSchoolYear = assessmentCalendar?.schoolYear;
  
  const [scores, setScores] = useState<ScoreData>({
    dyslexiaRiskIndicator: "",
    dyslexiaRiskCategory: "",
    armScore: "",
    armScorePR: "",
    equatedWCPM: "",
    equatedWCPMPR: "",
    phonAwareScore: "",
    phonAwareScore_PR: "",
    decodeScore: "",
    decodeScore_PR: "",
    esriScore: "",
    esriScore_PR: "",
    vocabularyScreenerScore: "",
    vocabularyScreenerScore_PR: "",
    compScore: "",
    compScore_PR: "",
    bkScore: "",
    bkScore_PR: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Partial<ScoreData>>({});
  
  // Assignment type logic - inherit from district assignment or allow teacher to choose
  const isDistrictAssignment = selectedWindow?.assignmentId != null;
  const [assignmentType, setAssignmentType] = useState<AssignmentType>(AssignmentType.PROGRESS_MONITORING);

  const handleBenchmarkChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setAssignmentType(AssignmentType.BENCHMARK);
    } else {
      setAssignmentType(AssignmentType.PROGRESS_MONITORING);
    }
  };

  const handleScoreChange = (field: keyof ScoreData, value: string) => {
    // Clear any existing error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
    
    // Allow empty string or valid numbers/text based on field type
    if (field === 'dyslexiaRiskCategory') {
      // Allow text for category (convert to uppercase)
      setScores(prev => ({ ...prev, [field]: value.toUpperCase() }));
    } else if (value === "" || /^-?\d*\.?\d*$/.test(value)) {
      // Allow numbers for scores/indicators/percentiles (including negative numbers)
      setScores(prev => ({ ...prev, [field]: value }));
    }
  };

  const validateScores = (): boolean => {
    const newErrors: Partial<ScoreData> = {};
    let isValid = true;

    // Check that all required fields are populated
    const allFields: (keyof ScoreData)[] = [
      'dyslexiaRiskIndicator', 'dyslexiaRiskCategory',
      'armScore', 'armScorePR',
      'equatedWCPM', 'equatedWCPMPR',
      'phonAwareScore', 'phonAwareScore_PR',
      'decodeScore', 'decodeScore_PR',
      'esriScore', 'esriScore_PR',
      'vocabularyScreenerScore', 'vocabularyScreenerScore_PR',
      'compScore', 'compScore_PR',
      'bkScore', 'bkScore_PR'
    ];

    allFields.forEach(field => {
      if (!scores[field] || scores[field].trim() === "") {
        newErrors[field] = "This field is required";
        isValid = false;
      }
    });

    // If any required fields are missing, skip format validation
    if (!isValid) {
      setErrors(newErrors);
      return isValid;
    }

    // Validate dyslexia risk indicator (should be numeric)
    if (scores.dyslexiaRiskIndicator && (isNaN(Number(scores.dyslexiaRiskIndicator)) || Number(scores.dyslexiaRiskIndicator) < 0 || Number(scores.dyslexiaRiskIndicator) > 99)) {
      newErrors.dyslexiaRiskIndicator = "Enter a score from 0 to 99 ";
      isValid = false;
    }

    // Validate dyslexia risk category (should be "D" or "SD")
    if (scores.dyslexiaRiskCategory && !['D', 'SD'].includes(scores.dyslexiaRiskCategory.toUpperCase())) {
      newErrors.dyslexiaRiskCategory = "Enter 'D' or 'SD'";
      isValid = false;
    }

    // Validate WCPM score (0 to 250)
    if (scores.equatedWCPM && (isNaN(Number(scores.equatedWCPM)) || Number(scores.equatedWCPM) < 0 || Number(scores.equatedWCPM) > 250)) {
      newErrors.equatedWCPM = "Enter a score from 0 to 250";
      isValid = false;
    }

    // Validate numeric score fields (excluding WCPM which has its own validation)
    const scoreFields: (keyof ScoreData)[] = ['armScore', 'phonAwareScore', 'decodeScore', 'esriScore', 'vocabularyScreenerScore', 'compScore', 'bkScore'];
    scoreFields.forEach(field => {
      if (scores[field] && (isNaN(Number(scores[field])) || Number(scores[field]) < -1.5 || Number(scores[field]) > 10.0)) {
        newErrors[field] = "Enter a score from -1.5 to 10.0";
        isValid = false;
      }
    });

    // Validate percentile fields (0-100)
    const percentileFields: (keyof ScoreData)[] = ['armScorePR', 'equatedWCPMPR', 'phonAwareScore_PR', 'decodeScore_PR', 'esriScore_PR', 'vocabularyScreenerScore_PR', 'compScore_PR', 'bkScore_PR'];
    percentileFields.forEach(field => {
      if (scores[field]) {
        const num = Number(scores[field]);
        if (isNaN(num) || num < 1 || num > 99) {
          newErrors[field] = "Enter a percentile from 1-99";
          isValid = false;
        }
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  const formatTestingWindow = () => {
    if (!selectedWindow?.startDate || !selectedWindow?.endDate) {
      return "Current Testing Window";
    }
    
    // Parse dates in UTC for consistent timezone handling
    const parseUTCDate = (dateString: string) => {
      const [year, month, day] = dateString.split('-').map(Number);
      return new Date(Date.UTC(year, month - 1, day)); // month is 0-indexed
    };
    
    const startDate = parseUTCDate(selectedWindow.startDate);
    const endDate = parseUTCDate(selectedWindow.endDate);
    const formatDate = (date: Date) => `${date.getUTCMonth() + 1}/${date.getUTCDate()}`;
    
    return `${selectedWindow.screeningWindowType || 'Assessment'} Window (${formatDate(startDate)} - ${formatDate(endDate)})`;
  };

  const handleSubmit = async () => {
    if (!validateScores()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Step 1: Create a student assignment (parented to district assignment if available)
      // Determine assignment type: inherit from district assignment or use teacher choice
      const finalAssignmentType = isDistrictAssignment 
        ? (selectedWindow?.assignmentType === AssignmentType.BENCHMARK ? ASSIGNMENT_TYPE.BENCHMARK : ASSIGNMENT_TYPE.PROGRESS_MONITORING)
        : assignmentType === AssignmentType.BENCHMARK 
          ? ASSIGNMENT_TYPE.BENCHMARK 
          : ASSIGNMENT_TYPE.PROGRESS_MONITORING;

      // Add appropriate tag based on assignment type
      const isBenchmarkType = isDistrictAssignment 
        ? selectedWindow?.assignmentType === AssignmentType.BENCHMARK
        : assignmentType === AssignmentType.BENCHMARK;
        
      const assignmentTags = isBenchmarkType
        ? ["MANUAL_ENTRY", "benchmark"]
        : ["MANUAL_ENTRY", "progress_monitoring"];

      const assignmentPayload = {
        entityIdList: [student.studentId],
        entityType: ENTITY_TYPE.STUDENT,
        assignmentType: finalAssignmentType,
        manifest: [
          {
            activityType: "assessment",
            locale: "EN_US",
            sequence: "CONTINUE",
            storyId: "11111111-1111-1111-1111-111111111111"
          }
        ],
        assignor: String(districtId),
        parentId: isDistrictAssignment ? assignmentId : undefined,
        schoolYear: currentSchoolYear,
        periodId: selectedWindow?.value,
        dateFrom: selectedWindow?.startDate,
        dateTo: selectedWindow?.endDate,
        tags: assignmentTags
      } as any;

      const assignmentResponse = await addBatchAssignments(assignmentPayload);
      
      if (!assignmentResponse?.batchAddAssignment || !assignmentResponse.batchAddAssignment[0]?.assignmentId) {
        throw new Error("Failed to create assignment");
      }

      const newAssignmentId = assignmentResponse.batchAddAssignment[0].assignmentId;

      // Step 2: Create activity (parented to the new assignment)  
      const createActivityData = await (SRS as any).createActivity(
        "assessment",
        student.studentId,
        "11111111-1111-1111-1111-111111111111", // GUID format with all 1s
        activityStatus.SCORING_NOT_STARTED,
        null,
        null,
        String(districtId),
        String(schoolId),
        ["NO_EDM", "NO_QA", "NO_SCALING"], // Tags to bypass scoring and quality checks
        newAssignmentId
      );

      if (!createActivityData?.data?.putActivity?.activityId) {
        throw new Error("Failed to create activity");
      }

      const assessmentActivityId = createActivityData.data.putActivity.activityId;

      // Step 2a: Add MANUAL_ENTRY tag to assessment activity
      await (SRS as any).updateActivityTags(assessmentActivityId, ["MANUAL_ENTRY"]);

      // Step 2b: Create dyslexia activity (parented to the same assignment)
      const createDyslexiaActivityData = await (SRS as any).createActivity(
        "dyslexia",
        student.studentId,
        "11111111-1111-1111-1111-111111111111", // GUID format with all 1s
        activityStatus.SCORING_NOT_STARTED,
        null,
        null,
        String(districtId),
        String(schoolId),
        ["NO_EDM", "NO_QA", "NO_SCALING"], // Tags to bypass scoring and quality checks
        newAssignmentId
      );

      if (!createDyslexiaActivityData?.data?.putActivity?.activityId) {
        throw new Error("Failed to create dyslexia activity");
      }

      const dyslexiaActivityId = createDyslexiaActivityData.data.putActivity.activityId;

      // Step 2c: Add MANUAL_ENTRY tag to dyslexia activity
      await (SRS as any).updateActivityTags(dyslexiaActivityId, ["MANUAL_ENTRY"]);
      
      // Step 3: Score the assessment activity using createAmiraScoreTable, updateAmiraScoreTable and existing SRS methods
      // First create the score table entry for assessment activity
      await (SRS as any).createAmiraScoreTable({ activityId: assessmentActivityId });
      
      // Then update the detailed scores using updateAmiraScoreTable for assessment
      const assessmentScoreTableInput = {
        activityId: assessmentActivityId,
        ...(scores.dyslexiaRiskIndicator && { dyslexiaRiskIndicator: Number(scores.dyslexiaRiskIndicator) }),
        ...(scores.dyslexiaRiskCategory && { dyslexiaRiskCategory: scores.dyslexiaRiskCategory }),
        ...(scores.armScore && { armScore: Number(scores.armScore) }),
        ...(scores.armScorePR && { armScorePR: Number(scores.armScorePR) }),
        ...(scores.equatedWCPM && { equatedWCPM: Number(scores.equatedWCPM) }),
        ...(scores.equatedWCPMPR && { equatedWCPMPR: Number(scores.equatedWCPMPR) }),
        ...(scores.phonAwareScore && { phonAwareScore: Number(scores.phonAwareScore) }),
        ...(scores.phonAwareScore_PR && { phonAwareScore_PR: Number(scores.phonAwareScore_PR) }),
        ...(scores.decodeScore && { decodeScore: Number(scores.decodeScore) }),
        ...(scores.decodeScore_PR && { decodeScore_PR: Number(scores.decodeScore_PR) }),
        ...(scores.esriScore && { esriScore: Number(scores.esriScore) }),
        ...(scores.esriScore_PR && { esriScore_PR: Number(scores.esriScore_PR) }),
        ...(scores.vocabularyScreenerScore && { vocabularyScreenerScore: Number(scores.vocabularyScreenerScore) }),
        ...(scores.vocabularyScreenerScore_PR && { vocabularyScreenerScore_PR: Number(scores.vocabularyScreenerScore_PR) }),
        ...(scores.compScore && { compScore: Number(scores.compScore) }),
        ...(scores.compScore_PR && { compScore_PR: Number(scores.compScore_PR) }),
        ...(scores.bkScore && { bkScore: Number(scores.bkScore) }),
        ...(scores.bkScore_PR && { bkScore_PR: Number(scores.bkScore_PR) }),
      };

      await (SRS as any).updateAmiraScoreTable(assessmentScoreTableInput);
      
      // Update assessment activity status to "scored" and display status to "completed"
      await (SRS as any).updateActivityStatus(
        assessmentActivityId,
        activityStatus.SCORED,
        activityStatus.COMPLETED,
        1
      );

      // Step 4: Score the dyslexia activity
      // Create score table entry for dyslexia activity
      await (SRS as any).createAmiraScoreTable({ activityId: dyslexiaActivityId });
      
      // Update the detailed scores for dyslexia activity (focusing on dyslexia-related scores)
      const dyslexiaScoreTableInput = {
        activityId: dyslexiaActivityId,
        ...(scores.dyslexiaRiskIndicator && { dyslexiaRiskIndicator: Number(scores.dyslexiaRiskIndicator) }),
        ...(scores.dyslexiaRiskCategory && { dyslexiaRiskCategory: scores.dyslexiaRiskCategory }),
        ...(scores.phonAwareScore && { phonAwareScore: Number(scores.phonAwareScore) }),
        ...(scores.phonAwareScore_PR && { phonAwareScore_PR: Number(scores.phonAwareScore_PR) }),
        ...(scores.decodeScore && { decodeScore: Number(scores.decodeScore) }),
        ...(scores.decodeScore_PR && { decodeScore_PR: Number(scores.decodeScore_PR) }),
        ...(scores.bkScore && { bkScore: Number(scores.bkScore) }),
        ...(scores.bkScore_PR && { bkScore_PR: Number(scores.bkScore_PR) }),
      };

      await (SRS as any).updateAmiraScoreTable(dyslexiaScoreTableInput);
      
      // Update dyslexia activity status to "scored" and display status to "completed"
      await (SRS as any).updateActivityStatus(
        dyslexiaActivityId,
        activityStatus.SCORED,
        activityStatus.COMPLETED,
        1
      );

      // Step 5: Update the assignment status to "DONE" so it shows as "Complete" in the UI
      await updateBatchAssignments([{
        entityId: student.studentId,
        entityType: ENTITY_TYPE.STUDENT,
        assignmentId: newAssignmentId,
        status: "DONE"
      }]);

      makeToast(
        `Scores successfully entered for ${student.firstName} ${student.lastName}`,
        ToastTypes.success,
        true,
        5000
      );

      if (onScoreSubmitted) {
        onScoreSubmitted();
      }
      
      onClose();
    } catch (error) {
      console.error("Error submitting scores:", error);
      makeToast(
        "Failed to submit scores. Please check your network connection and try again.",
        ToastTypes.warning,
        true,
        5000
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setScores({
      dyslexiaRiskIndicator: "",
      dyslexiaRiskCategory: "",
      armScore: "",
      armScorePR: "",
      equatedWCPM: "",
      equatedWCPMPR: "",
      phonAwareScore: "",
      phonAwareScore_PR: "",
      decodeScore: "",
      decodeScore_PR: "",
      esriScore: "",
      esriScore_PR: "",
      vocabularyScreenerScore: "",
      vocabularyScreenerScore_PR: "",
      compScore: "",
      compScore_PR: "",
      bkScore: "",
      bkScore_PR: "",
    });
    setErrors({});
    setAssignmentType(AssignmentType.PROGRESS_MONITORING);
    onClose();
  };



  return (
    <ModalPortal
      isOpen={isOpen}
      onClose={handleCancel}
      size="lg"
      overlayKeysCloseOverride={true}
    >
      <div className="comprehensive-score-entry-modal">
        <div className="modal-header-custom">
          <h2 className="modal-title-custom">Manually Enter {student.firstName} {student.lastName}'s Scores</h2>
          <button className="close-button" onClick={handleCancel}>×</button>
        </div>
        
        <div className="modal-body-custom">
          <div className="testing-window-section">
            <label className="testing-window-label">TESTING WINDOW</label>
            <div className="testing-window-dropdown">
              {formatTestingWindow()}
            </div>
          </div>

          {/* Assignment Type Selection - only show if not a district assignment */}
          {!isDistrictAssignment && (
            <div className="assignment-type-section">
              <div className="d-flex align-items-center">
                <input
                  type="checkbox"
                  id="benchmarkCheckbox"
                  className="me-2"
                  onChange={handleBenchmarkChange}
                  checked={assignmentType === AssignmentType.BENCHMARK}
                  disabled={isSubmitting}
                />
                <span style={{ color: '#262626', fontSize: '1.125rem', fontWeight: '600' }}>
                  Assessment is a Benchmark
                </span>
              </div>
              <div className="d-flex flex-column">
                <span style={{ color: '#262626', fontSize: '0.875rem' }}>
                  (if unchecked, assessment will be treated as progress monitoring)
                </span>
              </div>
            </div>
          )}

          <div className="scores-section">
            {/* Dyslexia Risk Section */}
            <div className="scores-header">
              <div className="score-label-header">Dyslexia Screener</div>
              <div className="score-inputs-header">
                <div className="input-header">Indicator</div>
                <div className="input-header">Category</div>
              </div>
            </div>

            <div className="score-row">
              <div className="score-label">Dyslexia Risk</div>
              <div className="score-inputs">
                <div className="input-group">
                  <input
                    type="text"
                    value={scores.dyslexiaRiskIndicator}
                    onChange={(e) => handleScoreChange('dyslexiaRiskIndicator', e.target.value)}
                    placeholder="Enter indicator"
                    className={`score-input ${errors.dyslexiaRiskIndicator ? "error" : ""}`}
                    disabled={isSubmitting}
                  />
                  {errors.dyslexiaRiskIndicator && <div className="field-error">{errors.dyslexiaRiskIndicator}</div>}
                </div>
                <div className="input-group">
                  <input
                    type="text"
                    value={scores.dyslexiaRiskCategory}
                    onChange={(e) => handleScoreChange('dyslexiaRiskCategory', e.target.value)}
                    placeholder="Enter category"
                    className={`score-input ${errors.dyslexiaRiskCategory ? "error" : ""}`}
                    disabled={isSubmitting}
                  />
                  {errors.dyslexiaRiskCategory && <div className="field-error">{errors.dyslexiaRiskCategory}</div>}
                </div>
              </div>
            </div>

            <div className="scores-header">
              <div className="score-label-header">Score Name</div>
              <div className="score-inputs-header">
                <div className="input-header">Score</div>
                <div className="input-header">Percentile</div>
              </div>
            </div>

            <ScoreRow 
              label="Amira Reading Mastery (ARM)" 
              scoreField="armScore" 
              percentileField="armScorePR"
              scores={scores}
              errors={errors}
              isSubmitting={isSubmitting}
              onScoreChange={handleScoreChange}
            />
            
            <ScoreRow 
              label="Adjusted WCPM Score" 
              scoreField="equatedWCPM" 
              percentileField="equatedWCPMPR"
              scores={scores}
              errors={errors}
              isSubmitting={isSubmitting}
              onScoreChange={handleScoreChange}
            />
            
            <ScoreRow 
              label="Phonological Awareness" 
              scoreField="phonAwareScore" 
              percentileField="phonAwareScore_PR"
              scores={scores}
              errors={errors}
              isSubmitting={isSubmitting}
              onScoreChange={handleScoreChange}
            />
            
            <ScoreRow 
              label="Decoding" 
              scoreField="decodeScore" 
              percentileField="decodeScore_PR"
              scores={scores}
              errors={errors}
              isSubmitting={isSubmitting}
              onScoreChange={handleScoreChange}
            />
            
            <ScoreRow 
              label="High Frequency Words" 
              scoreField="esriScore" 
              percentileField="esriScore_PR"
              scores={scores}
              errors={errors}
              isSubmitting={isSubmitting}
              onScoreChange={handleScoreChange}
            />
            
            <ScoreRow 
              label="Vocabulary" 
              scoreField="vocabularyScreenerScore" 
              percentileField="vocabularyScreenerScore_PR"
              scores={scores}
              errors={errors}
              isSubmitting={isSubmitting}
              onScoreChange={handleScoreChange}
            />
            
            <ScoreRow 
              label="Structures & Reasoning" 
              scoreField="compScore" 
              percentileField="compScore_PR"
              scores={scores}
              errors={errors}
              isSubmitting={isSubmitting}
              onScoreChange={handleScoreChange}
            />
            
            <ScoreRow 
              label="Background Knowledge" 
              scoreField="bkScore" 
              percentileField="bkScore_PR"
              scores={scores}
              errors={errors}
              isSubmitting={isSubmitting}
              onScoreChange={handleScoreChange}
            />
          </div>
        </div>

        <div className="modal-footer-custom">
          <button
            className="cancel-button-custom"
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            className="submit-button-custom"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Submitting..." : "Submit"}
          </button>
        </div>
      </div>
    </ModalPortal>
  );
}; 